import dayjs, { Dayjs } from 'dayjs';
import { FC, useMemo, useState } from 'react';
import { Button } from '../../../../../../components/button/button';
import { Modal, ModalProps } from '../../../../../../components/modal/modal';
import { TextArea } from '../../../../../../components/textarea/textarea';
import { validate } from '../../../../../../helpers/validate';
import { useLang } from '../../../../../../hooks/use-lang';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import { PutCheckInCheckOutRequest } from '../../../../../../store/api/check-in-check-out-api';
import { useCheckInCheckOutStore } from '../../../../../../store/use-check-in-check-out-store';
import { findCheckIn } from '../check-in-check-out-helpers';
import s from './check-in-check-out-edit-modal.module.css';
import { CheckingTimeInput } from './check-in-time-input/checking-time-input';

interface Props extends ModalProps {}

// TODO: REFACTOR
export const CheckInCheckOutEditModal: FC<Props> = ({ onClose }) => {
  const { t, getLocale } = useLang();
  const { getQueryParams } = useQueryParams();
  const { checkins, putCheckInCheckOut, isLoadingFetch } = useCheckInCheckOutStore();

  const params = getQueryParams();
  const id = params.id;

  const checkin = useMemo(() => findCheckIn(checkins || [], id), [id, checkins]);

  const [formData, setFormData] = useState({
    new_start_time: '',
    new_end_time: '',
    reason: '',
  });

  const [endTime, setEndTime] = useState<Dayjs>();

  const employee = checkin?.staff;

  const handleChange = (name: string, value: string) => {
    setFormData((prev) => {
      let startTime = dayjs(checkin?.start_time);
      let endTime = dayjs(checkin?.end_time);

      if (name === 'new_start_time') {
        const [startTimeHours, startTimeMinutes] = value.split(':');
        startTime = startTime.set('hour', +startTimeHours).set('minute', +startTimeMinutes);

        const [endTimeHours, endTimeMinutes] = prev.new_end_time.split(':');
        endTime = endTime.set('hour', +endTimeHours).set('minute', +endTimeMinutes);
      }

      if (name === 'new_end_time') {
        const [endTimeHours, endTimeMinutes] = value.split(':');
        endTime = endTime.set('hour', +endTimeHours).set('minute', +endTimeMinutes);

        const [startTimeHours, startTimeMinutes] = prev.new_start_time.split(':');
        startTime = startTime.set('hour', +startTimeHours).set('minute', +startTimeMinutes);
      }

      if (startTime.isAfter(endTime)) {
        endTime = endTime.add(1, 'day');
        setEndTime(endTime);
      }

      return { ...prev, [name]: value };
    });
  };

  const handleTimeParse = (time: string, referenceTime: string | null) => {
    const [hours, minutes] = time.split(':').map(Number);
    return dayjs(referenceTime).set('hour', hours).set('minute', minutes);
  };

  const validateFields = () => {
    return validate({
      new_start_time: {
        rule: () => {
          if (!!formData.new_end_time && !formData.new_start_time) {
            return true;
          }

          return (
            !!formData.new_start_time &&
            handleTimeParse(formData.new_start_time, checkin!.start_time).isValid()
          );
        },
      },
      new_end_time: {
        rule: () => {
          if (!!formData.new_start_time && !formData.new_end_time) {
            return true;
          }

          return (
            !!formData.new_end_time &&
            handleTimeParse(formData.new_end_time, checkin!.end_time).isValid()
          );
        },
      },
      reason: { rule: () => !!formData.reason.trim() },
    });
  };

  const handleSave = async () => {
    if (!checkin || !validateFields()) return;

    const body: Partial<PutCheckInCheckOutRequest> = {
      checkin_id: checkin.id,
      reason: formData.reason,
    };

    if (formData.new_start_time && checkin.start_time) {
      const [startTimeHours, startTimeMinutes] = formData.new_start_time.split(':');

      body.start_time = dayjs(checkin.start_time)
        .set('hour', +startTimeHours)
        .set('minute', +startTimeMinutes)
        .toISOString();
    }

    if (formData.new_end_time && checkin.end_time) {
      const [endTimeHours, endTimeMinutes] = formData.new_end_time.split(':');

      body.end_time = dayjs(checkin.end_time)
        .set('hour', +endTimeHours)
        .set('minute', +endTimeMinutes)
        .toISOString();
    }

    try {
      await putCheckInCheckOut(body as PutCheckInCheckOutRequest);
      setFormData({
        reason: '',
        new_start_time: '',
        new_end_time: '',
      });
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      className={s.feature}
      onClose={onClose}
      title={
        <h2 className={s.title}>
          {getLocale(employee?.surname)} {getLocale(employee?.name)}
        </h2>
      }
    >
      <div className={s.form}>
        <div className={s.inputs}>
          <CheckingTimeInput
            label={t('Приход')}
            date={dayjs(checkin?.start_time)}
            time={dayjs(checkin?.start_time)}
            newDate={dayjs(checkin?.start_time)}
            newTime={formData.new_start_time}
            name="start_time"
            onChange={handleChange}
            disabled={isLoadingFetch}
          />

          <CheckingTimeInput
            label={t('Уход')}
            date={dayjs(checkin?.end_time)}
            time={dayjs(checkin?.end_time)}
            newDate={
              endTime
                ? endTime
                : formData.new_end_time && checkin?.end_time
                ? dayjs(checkin?.end_time)
                    .set('hours', +formData.new_end_time.split(':')[0])
                    .set('minutes', +formData.new_end_time.split(':')[1])
                : undefined
            }
            newTime={formData.new_end_time}
            name="end_time"
            onChange={handleChange}
            disabled={!checkin?.end_time || isLoadingFetch}
          />
        </div>

        <TextArea
          name="reason"
          placeholder={t('Причина')}
          value={formData.reason}
          onChange={(e) => handleChange('reason', e.currentTarget.value)}
          disabled={isLoadingFetch}
        />
      </div>
      <Button size="lg" onClick={handleSave} disabled={isLoadingFetch}>
        {t('Сохранить изменения')}
      </Button>
    </Modal>
  );
};
